<template>
  <div class="template-custom-page-view">
    <iframe
      v-if="template"
      ref="frame"
      title="Cтраница"
      :class="['custom-page__frame']"
      scrolling="no"
      style="border: 0; width: 100%; height: 0%; display: block"
      :srcdoc="template"
      @changeContentHeight="setHeightCustomPage"
    />
  </div>
</template>

<script setup lang="ts">
import { useCustomPageStore } from '@/entities/custom-page';

import { useEvents, EVENTS } from '../model';

type ICustomEvent = Event & { detail: { height: number } };

const { id } = defineProps({
  id: {
    type: String,
    required: true,
  },
});

const frame = ref<HTMLIFrameElement | null>(null);

const setHeightCustomPage = (e: ICustomEvent) => {
  if (frame.value) {
    frame.value.style.height = `${(e as ICustomEvent).detail.height}px`;
  }
};

const emits = defineEmits(['openModal', 'scrollTo', 'openImage', 'navigateTo', 'closeModal']);

useEvents((eventName: keyof typeof EVENTS, payloads: any) => {
  if (eventName === EVENTS.showCustomPageModal) {
    emits('openModal', payloads);
  }

  if (eventName === EVENTS.scrollToElement) {
    emits('scrollTo', payloads);
  }

  if (eventName === EVENTS.navigate) {
    emits('navigateTo', payloads);
  }

  if (eventName === EVENTS.openImage) {
    emits('openImage', payloads);
  }

  if (eventName === EVENTS.closeModal) {
    emits('closeModal', payloads);
  }
});

const { render } = useCustomPageStore();

const wasAddedHeightChanger = ref(false);

const modify = (templ: string) => {
  const templateElement = new DOMParser().parseFromString(templ, 'text/html');
  const script = document.createElement('script');

  script.innerHTML = `
    function sendChangeContentHeightEvent() {
      const event = new CustomEvent('changeContentHeight', {
        detail: {
          height: document.documentElement.offsetHeight,
        },
      });

      window.frameElement.dispatchEvent(event);
    }

    window.onload = function() {
      const resizeObserver = new ResizeObserver(entries => {
        sendChangeContentHeightEvent();
      });

      resizeObserver.observe(document.body);
    };
  `;

  templateElement.head.appendChild(script);

  wasAddedHeightChanger.value = true;

  return templateElement.documentElement.outerHTML;
};

const { data: template } = useAsyncData(`template-component-page-${id}`, () => render(id), {
  transform: (data: string) => {
    if (import.meta.server) {
      return data;
    }

    return modify(data);
  },
});

onMounted(() => {
  if (!wasAddedHeightChanger.value) {
    template.value = modify(template.value || '');
  }
});

onUnmounted(() => {
  template.value = undefined;
});
</script>

<style lang="scss" scoped>
.template-custom-page-view {
  width: 100%;
  overflow-y: auto;
  scrollbar-width: none;
}
</style>
